import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import {
  deleteTimelineEvent,
  duplicateTimelineEvent,
  postFastEditEvent,
} from './calendarService';
import { TIMELINE_QUERY_KEY } from './calendarQueries';
import {
  applyFastEditToTimelineData,
  removeEventFromTimelineData,
} from './calendarCachePatch';
import useToast from '../../components/molecules/Toast/useToast';

/**
 * @typedef {[import('@tanstack/react-query').QueryKey, ApiManagerTimeline | undefined]} TimelineQueryDataTuple
 */

/**
 * @typedef {object} FastEditEventMutationVariables
 * @property {string} fastEditUrl
 * @property {string} eventId
 * @property {ApiManagerFastEditEventCreate} payload
 */

/**
 * @param {import('@tanstack/react-query').UseMutationOptions<unknown, Error, FastEditEventMutationVariables, unknown>} [mutationOptions]
 * @returns {import('@tanstack/react-query').UseMutationResult}
 */
export const useFastEditEventOptimistic = (mutationOptions = {}) => {
  const queryClient = useQueryClient();
  const { notify } = useToast();
  const { t } = useTranslation();

  const {
    onSuccess, onError, onSettled, ...restOptions
  } = mutationOptions;

  return useMutation({
    mutationFn: ({ fastEditUrl, payload }) => postFastEditEvent(fastEditUrl, payload),
    ...restOptions,
    onMutate: async ({ eventId, payload }) => {
      const arrayTimelineData = /** @type {TimelineQueryDataTuple[]} */ (queryClient.getQueriesData({
        queryKey: [TIMELINE_QUERY_KEY],
        type: 'active',
      }));

      const snapshots = arrayTimelineData.map(([queryKey, data]) => ({
        queryKey,
        previousData: data,
      }));

      arrayTimelineData.forEach(([queryKey, data]) => {
        if (!data) return;
        const patched = applyFastEditToTimelineData(data, eventId, {
          start: payload.start,
          end: payload.end,
          toResourceId: payload.toResourceId,
        });
        queryClient.setQueryData(queryKey, patched);
      });

      await queryClient.cancelQueries({ queryKey: [TIMELINE_QUERY_KEY], type: 'active' });

      return { snapshots };
    },
    onSuccess: (data, variables, context) => {
      onSuccess?.(data, /** @type {FastEditEventMutationVariables} */ (variables), context);
    },
    onError: (error, variables, context) => {
      const snapshots = /** @type {{ snapshots: Array<{ queryKey: import('@tanstack/react-query').QueryKey, previousData: unknown }> } | undefined} */ (
        context
      )?.snapshots;
      snapshots?.forEach(({ queryKey, previousData }) => {
        queryClient.setQueryData(queryKey, previousData);
      });

      notify({
        message: error?.message || t('toast.timeline.error'),
        type: 'alert',
      });
      onError?.(error, /** @type {FastEditEventMutationVariables} */ (variables), context);
    },
    onSettled: async (data, error, variables, context) => {
      await queryClient.invalidateQueries({ queryKey: [TIMELINE_QUERY_KEY], type: 'active' });
      onSettled?.(data, error, /** @type {FastEditEventMutationVariables} */ (variables), context);
    },
  });
};

/**
 * @typedef {object} DeleteTimelineEventMutationVariables
 * @property {string} deleteUrl
 * @property {string} eventId
 */

/**
 * @param {import('@tanstack/react-query').UseMutationOptions<unknown, Error, DeleteTimelineEventMutationVariables, unknown>} [mutationOptions]
 * @returns {import('@tanstack/react-query').UseMutationResult}
 */
export const useDeleteTimelineEventOptimistic = (mutationOptions = {}) => {
  const queryClient = useQueryClient();
  const { notify } = useToast();
  const { t } = useTranslation();

  const {
    onSuccess, onError, onSettled, ...restOptions
  } = mutationOptions;

  return useMutation({
    mutationFn: ({ deleteUrl }) => deleteTimelineEvent(deleteUrl),
    ...restOptions,
    onMutate: async ({ eventId }) => {
      await queryClient.cancelQueries({ queryKey: [TIMELINE_QUERY_KEY], type: 'active' });

      const arrayTimelineData = /** @type {TimelineQueryDataTuple[]} */ (queryClient.getQueriesData({
        queryKey: [TIMELINE_QUERY_KEY],
        type: 'active',
      }));

      const snapshots = arrayTimelineData.map(([queryKey, data]) => ({
        queryKey,
        previousData: data,
      }));

      arrayTimelineData.forEach(([queryKey, data]) => {
        if (!data) return;
        queryClient.setQueryData(queryKey, removeEventFromTimelineData(data, eventId));
      });

      return { snapshots };
    },
    onSuccess: (data, variables, context) => {
      onSuccess?.(data, /** @type {DeleteTimelineEventMutationVariables} */ (variables), context);
    },
    onError: (error, variables, context) => {
      const snapshots = /** @type {{ snapshots: Array<{ queryKey: import('@tanstack/react-query').QueryKey, previousData: unknown }> } | undefined} */ (
        context
      )?.snapshots;
      snapshots?.forEach(({ queryKey, previousData }) => {
        queryClient.setQueryData(queryKey, previousData);
      });

      notify({
        message: error?.message || t('toast.timeline.error'),
        type: 'alert',
      });
      onError?.(error, /** @type {DeleteTimelineEventMutationVariables} */ (variables), context);
    },
    onSettled: async (data, error, variables, context) => {
      await queryClient.invalidateQueries({ queryKey: [TIMELINE_QUERY_KEY], type: 'active' });
      onSettled?.(data, error, /** @type {DeleteTimelineEventMutationVariables} */ (variables), context);
    },
  });
};

/**
 * @typedef {object} DuplicateTimelineEventMutationVariables
 * @property {string} duplicateUrl
 */

/**
 * @param {import('@tanstack/react-query').UseMutationOptions<unknown, Error, DuplicateTimelineEventMutationVariables, unknown>} [mutationOptions]
 * @returns {import('@tanstack/react-query').UseMutationResult}
 */
export const useDuplicateTimelineEventOptimistic = (mutationOptions = {}) => {
  const queryClient = useQueryClient();
  const { notify } = useToast();
  const { t } = useTranslation();

  const {
    onSuccess, onError, onSettled, ...restOptions
  } = mutationOptions;

  return useMutation({
    mutationFn: ({ duplicateUrl }) => duplicateTimelineEvent(duplicateUrl),
    ...restOptions,
    onError: (error, variables, context) => {
      notify({
        message: error?.message || t('toast.timeline.error'),
        type: 'alert',
      });
      onError?.(error, /** @type {DuplicateTimelineEventMutationVariables} */ (variables), context);
    },
    onSettled: async (data, error, variables, context) => {
      await queryClient.invalidateQueries({ queryKey: [TIMELINE_QUERY_KEY], type: 'active' });
      onSettled?.(data, error, /** @type {DuplicateTimelineEventMutationVariables} */ (variables), context);
    },
  });
};
